// Sidebar project tree (§11.1, Unit 8 done-criteria).
//
//   ▼ Class 10B · March 2025
//     [●] Anna Müller  ← pulsating accent on processing
//         OCR · page 3 of 6 ← stage label, 11px --text-dim
//     [○] Ben Schmidt
//     [✓] Clara Yıldız
//   ▸ Class 10A · February 2025
//
// Selection state lives in URL search params (?project=42 / ?paper=42) so
// deep-linking to a selected project survives reloads, per C2 of the spec.
// Expanded state persists per-project to localStorage. First-load fallback
// expands the most-recently-created project.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { Paper, PaperStatus, Project } from "../api/types";
import { formatNp } from "../lib/score";
import { EXAM_PAPER_ID, MODEL_PAPER_ID } from "./DetailsPanel";

import "./ProjectTree.css";

const EXPANDED_STORAGE_KEY = "ophelia.tree.expanded";

function loadExpanded(): { map: Record<string, boolean>; firstEver: boolean } {
  if (typeof window === "undefined") return { map: {}, firstEver: true };
  try {
    const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
    if (raw === null) return { map: {}, firstEver: true };
    return { map: JSON.parse(raw) as Record<string, boolean>, firstEver: false };
  } catch {
    return { map: {}, firstEver: true };
  }
}

function saveExpanded(map: Record<string, boolean>): void {
  window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(map));
}

export interface ProjectTreeProps {
  projects: Project[];
}

export function ProjectTree({ projects }: ProjectTreeProps) {
  const initial = useRef(loadExpanded());
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => initial.current.map);
  const autoExpanded = useRef(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedProjectId = params.get("project");
  const selectedPaperId = params.get("paper");

  // First-load MRU fallback: only when this user has never expanded anything
  // (no localStorage entry exists yet) AND the API has returned projects.
  // Persists immediately so a refetch doesn't re-trigger this.
  useEffect(() => {
    if (autoExpanded.current) return;
    if (projects.length === 0) return;
    if (!initial.current.firstEver) {
      autoExpanded.current = true;
      return;
    }
    // The API returns ORDER BY created_at DESC, so projects[0] is the MRU.
    const mru = projects[0];
    autoExpanded.current = true;
    setExpanded((prev) => {
      const next = { ...prev, [String(mru.id)]: true };
      saveExpanded(next);
      return next;
    });
  }, [projects]);

  // Auto-expand the project that becomes selected via URL navigation (tour,
  // back-from-review, shared link). No-op if already expanded.
  useEffect(() => {
    if (!selectedProjectId) return;
    setExpanded((prev) => {
      if (prev[selectedProjectId]) return prev;
      const next = { ...prev, [selectedProjectId]: true };
      saveExpanded(next);
      return next;
    });
  }, [selectedProjectId]);

  // Pick up pending expand-state from localStorage for any *new* project ids
  // that arrive in the list. Lets the Create-Project flow write a single
  // localStorage entry then trigger an SWR mutate, and have the tree
  // auto-expand the new row without a custom event bus.
  useEffect(() => {
    if (projects.length === 0) return;
    const fresh = loadExpanded().map;
    setExpanded((prev) => {
      let changed = false;
      const merged = { ...prev };
      for (const project of projects) {
        const id = String(project.id);
        if (!(id in prev) && id in fresh) {
          merged[id] = fresh[id];
          changed = true;
        }
      }
      return changed ? merged : prev;
    });
  }, [projects]);

  function toggleProject(p: Project) {
    setExpanded((prev) => {
      const next = { ...prev, [String(p.id)]: !prev[String(p.id)] };
      saveExpanded(next);
      return next;
    });
  }

  function selectProject(p: Project) {
    const next = new URLSearchParams(params);
    if (selectedProjectId === String(p.id) && !selectedPaperId) {
      // Click selected project again → deselect (matches §11.6 deselection).
      next.delete("project");
      next.delete("paper");
    } else {
      next.set("project", String(p.id));
      next.delete("paper");
    }
    setParams(next, { replace: true });
  }

  function selectPaper(paper: Paper, projectId: number) {
    // Single click selects (updates details panel); double-click opens review.
    const next = new URLSearchParams(params);
    next.set("project", String(projectId));
    next.set("paper", String(paper.id));
    setParams(next, { replace: true });
  }

  function selectModel(projectId: number) {
    const next = new URLSearchParams(params);
    next.set("project", String(projectId));
    next.set("paper", MODEL_PAPER_ID);
    setParams(next, { replace: true });
  }

  function selectExam(projectId: number) {
    const next = new URLSearchParams(params);
    next.set("project", String(projectId));
    next.set("paper", EXAM_PAPER_ID);
    setParams(next, { replace: true });
  }

  function openPaperReview(paper: Paper) {
    navigate(`/review/${paper.id}`);
  }

  // Display oldest-first so newly created projects appear at the bottom.
  // "Getting Started" is always pinned to the top regardless of creation date.
  const items = useMemo(() => {
    const reversed = [...projects].reverse();
    const gsIdx = reversed.findIndex((p) => p.name === "Getting Started");
    if (gsIdx <= 0) return reversed;
    const gs = reversed[gsIdx];
    return [gs, ...reversed.filter((_, i) => i !== gsIdx)];
  }, [projects]);

  if (items.length === 0) {
    return (
      <div className="tree-empty">
        <p>No projects yet.</p>
        <p className="tree-empty__hint">Use + New Project to begin.</p>
      </div>
    );
  }

  return (
    <ul className="tree" role="tree">
      {items.map((project) => {
        const isExpanded = !!expanded[String(project.id)];
        const isSelected =
          selectedProjectId === String(project.id) && !selectedPaperId;
        const isSample = project.name === "Getting Started";
        const total = project.papers.length;
        const allCompleted = total > 0 && project.papers.every((p) => p.status === "completed");
        return (
          <li key={project.id} className="tree__project" role="treeitem" aria-expanded={isExpanded}>
            <div
              className={`tree__project-row${isSelected ? " tree__row--selected" : ""}`}
              onClick={() => selectProject(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectProject(project);
                }
              }}
            >
              <span
                className="tree__chevron"
                aria-hidden
                onClick={(e) => {
                  e.stopPropagation();
                  toggleProject(project);
                }}
              >
                {isExpanded ? "▼" : "▶"}
              </span>
              <span className="tree__project-name">
                {isSample ? "✦ " : ""}
                {project.name}
              </span>
              <span
                className={`tree__proj-dot tree__proj-dot--${allCompleted ? "complete" : "active"}`}
                aria-hidden
              />
            </div>

            {isExpanded && (
              <ul className="tree__papers" role="group">
                <ExamRow
                  selected={selectedPaperId === EXAM_PAPER_ID}
                  onSelect={() => selectExam(project.id)}
                />
                <ModelRow
                  selected={selectedPaperId === MODEL_PAPER_ID}
                  onSelect={() => selectModel(project.id)}
                />
                {project.papers.length === 0
                  ? <li className="tree__empty-papers">No papers yet</li>
                  : project.papers.map((paper) => (
                      <PaperRow
                        key={paper.id}
                        paper={paper}
                        project={project}
                        selected={selectedPaperId === String(paper.id)}
                        onSelect={() => selectPaper(paper, project.id)}
                        onOpen={() => openPaperReview(paper)}
                      />
                    ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface PaperRowProps {
  paper: Paper;
  project: Project;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}

function PaperRow({ paper, project, selected, onSelect, onOpen }: PaperRowProps) {
  // Grade is only known in the sidebar when the score is overridden — the tree
  // payload has no annotations to compute a live score from.
  const grade =
    paper.score_override != null ? formatNp(paper.score_override, project.score_format).label : null;

  return (
    <li
      className={`tree__paper${selected ? " tree__row--selected" : ""}`}
      role="treeitem"
      onClick={onSelect}
      onDoubleClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="tree__paper-row">
        <StatusDot status={paper.status} />
        <span className="tree__paper-name">{paper.name}</span>
        {grade && <span className="tree__paper-grade">{grade}</span>}
      </div>
      {paper.status === "processing" && (
        <div className="tree__progress" aria-hidden>
          <div className="tree__progress-bar" />
        </div>
      )}
    </li>
  );
}

function ExamRow({ selected, onSelect }: { selected: boolean; onSelect: () => void }) {
  return (
    <li
      className={`tree__paper${selected ? " tree__row--selected" : ""}`}
      role="treeitem"
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="tree__paper-row">
        <ExamIcon />
        <span className="tree__paper-name">Exam</span>
      </div>
    </li>
  );
}

// A little page with a folded corner — same footprint (8px) as the status dots.
function ExamIcon() {
  return (
    <svg
      className="tree__exam-icon"
      width="8"
      height="10"
      viewBox="0 0 11 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 1.5h5L9.5 5v6.5a.5.5 0 0 1-.5.5H2a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" />
      <path d="M7 1.5V5h2.5" />
    </svg>
  );
}

function ModelRow({ selected, onSelect }: { selected: boolean; onSelect: () => void }) {
  return (
    <li
      className={`tree__paper${selected ? " tree__row--selected" : ""}`}
      role="treeitem"
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="tree__paper-row">
        <span className="status-dot status-dot--model" aria-hidden />
        <span className="tree__paper-name">Model Solution</span>
      </div>
    </li>
  );
}

const STATUS_KEY: Record<PaperStatus, string> = {
  queued: "paper_status.queued",
  staged: "paper_status.staged",
  processing: "paper_status.processing",
  ready: "paper_status.ready",
  in_review: "paper_status.in_review",
  completed: "paper_status.completed",
  failed: "paper_status.failed",
  quota_exceeded: "paper_status.quota_exceeded",
};

function StatusDot({ status }: { status: PaperStatus }) {
  const { t } = useTranslation();
  const label = t(STATUS_KEY[status]);
  return (
    <span
      className={`status-dot status-dot--${status.replace("_", "-")}`}
      aria-label={label}
      title={label}
    />
  );
}
